import { Fragment } from 'react'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { GrAccessibility, GrCloudSoftware, GrMoney } from 'react-icons/gr'
import Trans from 'next-translate/Trans'
import { MarketingDrawer, SnackBar } from '@app/components/general'
import { MarketingIntroChecker } from '@app/components/stateless/marketing/intro-rest-checker'
import { metaSetter } from '@app/utils'
import { MarketingBottomTemporaryDrawer } from '@app/components/modal'
import { CtaInputRest } from '@app/components/cta/searchbar/cta-input-rest'
import { MarketingProducts } from '@app/components/stateless/marketing/products'
import { routeParseAbsolute } from '@app/lib/router-handler'
import { Link } from '@app/components/stateless/typo/link'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import type { I18nDictionary, Translate } from 'next-translate'
import { blueLink } from '@app/components/stateless/typo/link-style'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/website-accessibility-checker',
    }),
  }
}

const HeadBlock = ({ index, t }: { index: number; t: Translate }) => {
  const headEle = t(`info.${index}.header`)

  return (
    <div>
      {index === 0 ? (
        <h3 className='text-xl md:text-2xl font-bold'>{headEle}</h3>
      ) : (
        <h4 className='text-xl md:text-2xl font-bold'>{headEle}</h4>
      )}

      <p className='text-lg text-gray-700 dark:text-gray-100'>
        {t(`info.${index}.details`)}
      </p>
    </div>
  )
}

function WebsiteAccessibilityChecker() {
  const { t, lang } = useTranslation('website-accessibility-checker')

  return (
    <Fragment>
      <MarketingDrawer navPosition={'relative'}>
        <MarketingIntroChecker>
          <CtaInputRest />
        </MarketingIntroChecker>

        <div className='px-4 space-y-6 py-6'>
          <HeadBlock index={0} t={t} />
          <HeadBlock index={1} t={t} />
          <HeadBlock index={2} t={t} />
        </div>

        <div className='flex flex-col pb-1 pt-14 px-4 space-y-2'>
          <h5 className='m-0 text-xl md:text-3xl font-semibold'>
            {t('build-future')}
          </h5>
          <div className='grid lg:grid-cols-3 gap-y-6 gap-x-12 place-items-center py-2'>
            <div className='flex gap-x-1 gap-y-2 place-items-center'>
              <div className='flex place-items-center pr-6'>
                <GrAccessibility className='grIcon text-xl md:text-4xl' />
              </div>
              <div>
                <h6 className='text-lg md:text-xl font-semibold'>
                  {t(`sub-info.0.header`)}
                </h6>
                <p className='md:text-lg text-gray-800 dark:text-gray-200'>
                  {t(`sub-info.0.details`)}
                </p>
              </div>
            </div>
            <div className='flex gap-x-1 gap-y-2 place-items-center'>
              <div className='flex place-items-center pr-6'>
                <GrCloudSoftware className='grIcon text-xl md:text-4xl' />
              </div>
              <div>
                <h6 className='text-lg md:text-xl font-semibold'>
                  {t(`sub-info.1.header`)}
                </h6>
                <Trans
                  i18nKey={`website-accessibility-checker:sub-info.1.details`}
                  components={[
                    <p
                      key={'t-1'}
                      className='md:text-lg text-gray-800 dark:text-gray-200'
                    />,
                    <Link
                      className={blueLink}
                      key={'t-2'}
                      href={routeParseAbsolute('/api-info', lang)}
                    />,
                  ]}
                />
              </div>
            </div>
            <div className='flex gap-x-1 gap-y-2 place-items-center'>
              <div className='flex place-items-center pr-6'>
                <GrMoney className='grIcon text-xl md:text-4xl' />
              </div>
              <div>
                <h6 className='text-lg md:text-xl font-semibold'>
                  {t(`sub-info.2.header`)}
                </h6>
                <p className='md:text-lg text-gray-800 dark:text-gray-200'>
                  {t(`sub-info.2.details`)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <MarketingProducts />
      </MarketingDrawer>
      <SnackBar topLevel />
      <MarketingBottomTemporaryDrawer />
    </Fragment>
  )
}

export default metaSetter(
  { WebsiteAccessibilityChecker },
  {
    translation: 'website-accessibility-checker',
    rest: true,
  }
)
